import { createSlice } from '@reduxjs/toolkit';
import {
  settingsApi,
  UiSettingsModel,
  MeasurementUnit,
  useGetUiSettingsQuery,
} from '../apis/settingsApi';
import { MapProviderModel, MapProviderType } from '@cas/types/settings';
import { OSM_MAX_NATIVE_ZOOM } from '@cas/components/maps/config';
import { getBaseHref } from '@casops/bootstrap';
import { useAppSelector } from '../hooks';
import { useTranslation } from 'react-i18next';

// Localization fallback values.
const fallbackZoom = OSM_MAX_NATIVE_ZOOM;
const fallbackDateTimeFormat = 'en-AU';
const fallbackTimezone = 'Australia/Sydney';
const fallbackUnits = MeasurementUnit.Metric;

export const defaultSettings: UiSettingsModel = {
  siteName: null,
  appVersion: '',
  map: {
    providerType: MapProviderType.OpenStreetMap,
    uri: null,
    layerName: null,
    maxZoom: fallbackZoom,
    maxNativeZoom: fallbackZoom,
  },
  localization: {
    units: fallbackUnits,
    dateTimeNumberFormat: fallbackDateTimeFormat,
    timezone: fallbackTimezone,
  },
};

const baseHref = getBaseHref();
const rasterTileBaseUrl = `${baseHref}${baseHref.endsWith('/') ? '' : '/'}MapProviders/Raster`;

// Change the uri for any raster provider to use the local raster tile url.
const populateRasterTileUrl = (mapProvider: MapProviderModel): MapProviderModel => {
  if (
    [MapProviderType.RasterOnly, MapProviderType.RasterWithOpenStreetMap].includes(
      mapProvider.providerType,
    )
  ) {
    return { ...mapProvider, uri: rasterTileBaseUrl };
  }

  return mapProvider;
};

/**
 * This slice solely depends on the API response from `getUiSettings`.
 * The response is transformed here and saved for frequent state selection.
 * While the API response must remain unchanged for ease of form validation,
 * the data can be transformed here before it is saved to the store.
 */
const settingsSlice = createSlice({
  name: 'settings',
  initialState: { ...defaultSettings },
  reducers: {},
  extraReducers: builder => {
    builder.addMatcher(settingsApi.endpoints.getUiSettings.matchFulfilled, (state, { payload }) => {
      return {
        ...state,
        ...payload,
        map: {
          ...state.map,
          ...populateRasterTileUrl(payload.map),
        },
        localization: {
          ...state.localization,
          ...payload.localization,
        },
      };
    });
  },
});

export default settingsSlice.reducer;

export const useGetSiteName = () => {
  const { t } = useTranslation();

  // Fetch the UI settings so that they can be loaded into the settings slice.
  useGetUiSettingsQuery();

  const siteName = useAppSelector(state => state.settings.siteName);
  return siteName || t('Site'); // Fallback to a safe translation.
};
